import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {BaseComponent, CompoApiService, CrmIssue, IdAndHeader, PageApiResponse} from 'compob2b-libs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { HttpParams } from '@angular/common/http';
@Component({
    selector: 'app-payment-autocomplete',
    templateUrl: './payment-autocomplete.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PaymentAutocompleteComponent extends BaseComponent implements OnInit {
  @Input() issue: CrmIssue;
  @Input() unclosed: boolean = false;
  @Output() selectedPaymentEvent = new EventEmitter<IdAndHeader>();

  public loadingPayments = false;
  public payments: IdAndHeader[] = [];
  public paymentInput$ = new Subject<string>();
  public selectedPayment: any;

  public loadedElements = 0;
  public totalElements = 0;
  private pageSize = 30;
  private currentPage = 0;
  private totalPages = 0;
  private numberOfItemsFromEndBeforeFetchingMore = 10;

  private searchTerm = '';

  constructor(private readonly apiService: CompoApiService) {
    super();
  }

  ngOnInit(): void {
    this.selectedPayment = this.createExecutor(this.issue.subproject);

    this.paymentInput$
      .pipe(
        tap(term => this.searchTerm = term),
        debounceTime(400),
        distinctUntilChanged(),
        filter(term => term !== null),
        tap(_ => this.clearPreviousSearch()),
        switchMap(term => this.searchPayments(term)),
        takeUntil(this.unsubscribe),
      ).subscribe(foundPayments => this.setFoundPayments(foundPayments));

    this.loadAllPayments();
  }

  private loadAllPayments(): void {
    this.loadingPayments = true;

    const url = `/subproject/extended/page/0/25/id/desc/?project=${this.issue.crmProjectId}&unclosed=${this.unclosed}`;

    this.apiService.getWithParams<PageApiResponse<IdAndHeader[]>>(url, new HttpParams())
        .pipe(
            tap(response => this.handleResponse(response.data)),
            map(response => response.data.content),
            catchError(error => {
              console.error('Error loading payments:', error);
              return of([]);
            }),
            finalize(() => {
              this.loadingPayments = false;
              this.cdr.detectChanges();
            }),
            takeUntil(this.unsubscribe)
        )
        .subscribe(payments => {
          this.setFoundPayments(payments);
        });
  }


  private setFoundPayments(payments: IdAndHeader[]): void {
    this.payments = payments;
    this.cdr.detectChanges();
  }

  private clearPreviousSearch(): void {
    this.payments = [];
    this.totalPages = 0;
    this.totalElements = 0;
    this.loadedElements = 0;
    this.currentPage = 0;
  }

  private searchPayments(keyword: string, page: number = 0): Observable<IdAndHeader[]> {
    const url = `/subproject/autocomplete/project/${this.issue.crmProjectId}/page/${page}/${this.pageSize}`;
    const params = keyword ? new HttpParams().set('header', keyword) : new HttpParams();

    this.loadingPayments = true;
    return this.apiService.getWithParams<PageApiResponse<IdAndHeader[]>>(url, params)
      .pipe(
        tap(response => this.handleResponse(response.data)),
        map(response => response.data.content),
        tap(_ => this.loadingPayments = false),
      );
  }

  comparePayments(a: IdAndHeader, b: IdAndHeader): boolean {
    return a.id === b.id;
  }

  changeSelectedPayment(payment: IdAndHeader): void {
    this.selectedPaymentEvent.emit(payment);
  }

  private createExecutor(subproject: any): IdAndHeader {
    return subproject ? {
      id: subproject.id,
      header: subproject.header
    } : null;
  }

  private handleResponse(response: PageApiResponse<IdAndHeader[]>) {
    this.totalPages = response.totalPages;
    this.totalElements = response.totalElements;
    this.currentPage = response.number >= response.totalPages ? response.totalPages - 1 : response.number;
    this.loadedElements = this.payments.length + response.numberOfElements;
  }

  onScroll($event: { start: number; end: number }) {
    if (this.loadingPayments || this.loadedElements >= this.totalElements) {
      return;
    }

    if ($event.end + this.numberOfItemsFromEndBeforeFetchingMore >= this.loadedElements) {
      this.fetchMore();
    }
  }

  onScrollToEnd() {
    if (this.loadingPayments || this.loadedElements >= this.totalElements) {
      return;
    }

    this.fetchMore();
  }

  private fetchMore(): void {
    this.searchPayments(this.searchTerm, this.currentPage + 1)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(items => {
        this.payments = this.payments.concat(items);
        this.cdr.detectChanges();
      });
  }
}
